import { IDate } from '@models';
import {
  buildTimeOptions,
  EARLIEST_BOOKING_MINUTES,
  formatDateWithOffset,
  getCalendarStartHour,
  getCalendarStartMinutes,
  isTimeTbc,
  TBC_TIME,
} from '@utils';
import { Col, DatePicker, Row, Select } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';

interface IProps {
  dateTime: IDate | undefined;
  setDate: (value: IDate) => void;
  /** Lets the date be cleared; the caller decides what "no date" means (e.g. not scheduled). */
  onClear?: () => void;
  /** Shown but not editable - the value was decided elsewhere (e.g. by a calendar slot). */
  disabled?: boolean;
  /**
   * Offers "TBC" as a time - the day is known, the hour not yet (stored as midnight, see
   * isTimeTbc) - and makes it the time a freshly picked date gets. For booking slots; a drop-off
   * is always a real time.
   */
  allowTbc?: boolean;
  /**
   * Lets an earlier day be picked. For a place that already asks to be sure (the reschedule modal),
   * where putting a job back a day is a correction somebody knows they are making.
   */
  allowPast?: boolean;
}

export const DateTimeSelect: React.FC<IProps> = ({
  dateTime,
  setDate,
  onClear,
  disabled,
  allowTbc,
  allowPast,
}) => {
  const timeSelectOptions = useMemo(buildTimeSelectOptions, []);

  const handleDateChange = async (date: dayjs.Dayjs | null) => {
    if (!date) {
      onClear?.();
      return;
    }
    const current = dateTime?.startAt ? dayjs(dateTime.startAt) : undefined;
    const startH = current ? current.hour() : allowTbc ? 0 : getCalendarStartHour();
    const startM = current ? current.minute() : 0;
    const start = dayjs(date).set('hour', startH).set('minute', startM);
    setDate({ startAt: formatDateWithOffset(start) });
  };

  const handleTimeChange = async (value: string) => {
    const time = value.split(':');
    const start = dayjs(dateTime?.startAt).hour(Number(time[0])).minute(Number(time[1])).second(0);
    setDate({ startAt: formatDateWithOffset(start) });
  };

  // Empty until there is a date - an optional field must not look pre-filled. Midnight reads
  // as "TBC" where that is on offer.
  const hhmm = dateTime?.startAt ? dayjs(dateTime.startAt).format('HH:mm') : undefined;
  const isTbc = !!dateTime?.startAt && allowTbc && isTimeTbc(dateTime.startAt);
  const timeValue = !hhmm ? undefined : isTbc ? TBC_TIME : hhmm;

  // A time off the grid (e.g. "now" late in the evening) is shown as it is: showing another time
  // than the one the form holds would let the user confirm a value they cannot see.
  const offGrid = !!hhmm && !isTbc && !timeSelectOptions.some((time) => time.value === hhmm);
  const options = [
    ...(allowTbc ? [{ value: TBC_TIME, label: 'TBC' }] : []),
    ...(offGrid ? [{ value: hhmm, label: hhmm }] : []),
    ...timeSelectOptions,
  ];

  return (
    // The date takes a little more of the row than the time (DD/MM/YYYY is the longer text), 4 to 3.
    // The row is as wide as its box plus the gutter's two negative margins' worth: a plain 100% left
    // the last 8px of the box empty, a gap between the time and whatever sits beside it.
    <Row gutter={8} wrap={false} style={{ width: 'calc(100% + 8px)' }}>
      <Col flex="4 1 0%" style={{ minWidth: 0 }}>
        <DatePicker
          inputReadOnly
          disabled={disabled}
          showTime={false}
          allowClear={!!onClear}
          placeholder="Select date"
          disabledDate={allowPast ? undefined : (current) => current < dayjs().startOf('day')}
          format="DD/MM/YYYY"
          style={{ width: '100%' }}
          value={dateTime?.startAt ? dayjs(dateTime?.startAt) : undefined}
          onChange={handleDateChange}
        />
      </Col>
      <Col flex="3 1 0%" style={{ minWidth: 0 }}>
        <Select
          disabled={disabled}
          placeholder="Start time"
          style={{ width: '100%', height: 32 }}
          options={options}
          value={timeValue}
          onChange={(e) => handleTimeChange(e)}
        />
      </Col>
    </Row>
  );
};

// The tenant's calendar start time to 20:45 in 15 minute steps. Built per render rather than at
// import: the setting is only known once the app has loaded it.
const buildTimeSelectOptions = () =>
  buildTimeOptions(15, Math.max(getCalendarStartMinutes(), EARLIEST_BOOKING_MINUTES), 21 * 60);
